import React, { useState } from "react";
import { DateTime } from "luxon";
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid2 as Grid,
  Stack,
  Link,
  Card,
  Divider,
  CardActions,
  CardContent,
  IconButton,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download,
  CheckCircle,
  Cancel,
  Person,
  LocationOn,
  AccessTime,
  Description,
  FileDownload,
  PictureAsPdf,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import axios from "axios";

function PastNotarizationModal({
  open,
  onClose,
  selectedJob,
  currentUser,
  currentSession,
}) {
  const [downloading, setDownloading] = useState(false);
  const [downloadingType, setDownloadingType] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const utcDate = DateTime.fromISO(dateString, { zone: "utc" });
    const localDate = utcDate.setZone(currentUser?.timezone);
    return localDate.toFormat("MMMM dd, yyyy hh:mm a");
  };

  const getStatusInfo = () => {
    const isCompleted = selectedJob?.status === "completed";
    return {
      status: isCompleted ? "Completed Successfully" : "Cancelled",
      color: isCompleted ? "success" : "error",
      icon: isCompleted ? <CheckCircle /> : <Cancel />,
    };
  };

  const handleDownloadDocuments = async (type = "all") => {
    setDownloading(true);
    setDownloadingType(type);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/download-documents`,
        {
          jobId: selectedJob.id,
          documentType: type, // 'all', 'signed', 'original'
          currentSession,
        },
        {
          withCredentials: true,
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const filename =
        type === "all"
          ? `notarization_${selectedJob.id}_all_documents.zip`
          : type === "signed"
            ? `notarization_${selectedJob.id}_signed_documents.zip`
            : `notarization_${selectedJob.id}_original_documents.zip`;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(
        `${type === "all" ? "All documents" : type === "signed" ? "Signed documents" : "Original documents"} downloaded successfully!`
      );
    } catch (error) {
      setError(error.response?.data?.message || "Failed to download documents");
    } finally {
      setDownloading(false);
      setDownloadingType("");
    }
  };

  const statusInfo = getStatusInfo();

  if (!selectedJob) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", md: "80%", lg: "70%" },
          maxWidth: "1000px",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 10, top: 10, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={3}
            >
              <Typography variant="h4" fontWeight="bold">
                Past Notarization Details
              </Typography>
              <Chip
                icon={statusInfo.icon}
                label={statusInfo.status}
                color={statusInfo.color}
                size="large"
                sx={{ px: 2, py: 1, fontSize: "1rem" }}
              />
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                onClose={() => setSuccess("")}
              >
                {success}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Completion Information */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      <AccessTime sx={{ mr: 1, verticalAlign: "middle" }} />
                      Session Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Session Date
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {formatDateTime(selectedJob.booked_date)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Completion Date
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {formatDateTime(
                            selectedJob.completed_date ||
                              selectedJob.notarized_at
                          )}
                        </Typography>
                      </Box>
                      {selectedJob.status === "cancelled" &&
                        selectedJob.failure_reason && (
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                            >
                              Cancellation Reason
                            </Typography>
                            <Typography variant="body2" color="error">
                              {selectedJob.failure_reason}
                            </Typography>
                          </Box>
                        )}
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Duration
                        </Typography>
                        <Typography variant="body2">
                          {selectedJob.session_duration || "N/A"}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Signer Information */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      <Person sx={{ mr: 1, verticalAlign: "middle" }} />
                      Signer Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Full Name
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {selectedJob.signer?.fullName || "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Email Address
                        </Typography>
                        <Link
                          href={`mailto:${selectedJob.signer?.email}`}
                          color="inherit"
                        >
                          <Typography variant="body2">
                            {selectedJob.signer?.email || "N/A"}
                          </Typography>
                        </Link>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Phone Number
                        </Typography>
                        <Typography variant="body2">
                          {selectedJob.signer?.phone || "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Address
                        </Typography>
                        <Typography variant="body2">
                          {selectedJob.signer?.address || "N/A"}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Project & Document Information */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      <LocationOn sx={{ mr: 1, verticalAlign: "middle" }} />
                      Project Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Property Address
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {selectedJob.project_address ||
                            selectedJob.document?.property_address ||
                            "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Owner
                        </Typography>
                        <Typography variant="body2">
                          {selectedJob.document?.owner || "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          County
                        </Typography>
                        <Typography variant="body2">
                          {selectedJob.document?.county || "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Contractor
                        </Typography>
                        <Typography variant="body2">
                          {selectedJob.contractor?.name || "N/A"}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Documents Section */}
              {selectedJob.status === "completed" && (
                <Grid size={{ xs: 12 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <Description sx={{ mr: 1, verticalAlign: "middle" }} />
                        Available Documents
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 2 }}
                      >
                        Download the notarized documents from this completed
                        session:
                      </Typography>

                      <List>
                        <ListItem>
                          <ListItemIcon>
                            <PictureAsPdf color="error" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Signed & Notarized Documents"
                            secondary="Final documents with notary seal and signatures"
                          />
                          <LoadingButton
                            startIcon={<FileDownload />}
                            variant="outlined"
                            size="small"
                            onClick={() => handleDownloadDocuments("signed")}
                            loading={
                              downloading && downloadingType === "signed"
                            }
                          >
                            Download
                          </LoadingButton>
                        </ListItem>

                        <ListItem>
                          <ListItemIcon>
                            <Description color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Original Documents"
                            secondary="Original documents before notarization"
                          />
                          <LoadingButton
                            startIcon={<FileDownload />}
                            variant="outlined"
                            size="small"
                            onClick={() => handleDownloadDocuments("original")}
                            loading={
                              downloading && downloadingType === "original"
                            }
                          >
                            Download
                          </LoadingButton>
                        </ListItem>

                        <ListItem>
                          <ListItemIcon>
                            <Download color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Complete Package"
                            secondary="All documents in a single ZIP file"
                          />
                          <LoadingButton
                            startIcon={<FileDownload />}
                            variant="contained"
                            size="small"
                            onClick={() => handleDownloadDocuments("all")}
                            loading={downloading && downloadingType === "all"}
                          >
                            Download All
                          </LoadingButton>
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </CardContent>

          <CardActions sx={{ p: 3, pt: 0, justifyContent: "flex-end" }}>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
            {selectedJob.status === "completed" && (
              <LoadingButton
                startIcon={<Download />}
                variant="contained"
                color="primary"
                onClick={() => handleDownloadDocuments("all")}
                loading={downloading && downloadingType === "all"}
              >
                Download All Documents
              </LoadingButton>
            )}
          </CardActions>
        </Card>
      </Box>
    </Modal>
  );
}

export default PastNotarizationModal;
